import {
  ActionStatus,
  ActionType,
  IActionResponseItem,
} from "shared";
import { useSelector } from "react-redux";

import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { usePreviewActionQuery, useRegenerateAiEmailMutation } from "../../services/api/actionApi/action";
import { RootState } from "../../store";
import { LoadingOverlay } from "../Molecules/LoadingOverlay";
import { Card } from "../../../common/Atoms/Card";
import { CustomIframe } from "../IFrame";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { InvoiceLineItems } from "../../../common/Components/Invoice/InvoiceLineItems";
import { Notification } from "../../../common/Atoms/Notification";
import { EmailPreviewDetails } from "../Molecules/EmailPreviewDetails";
import { StatementEmailPreview } from "../Molecules/StatementEmailPreview";
import { Button } from "../../../common/Atoms/Button";

interface ActionPreviewSectionProps {
  action?: IActionResponseItem;
}

const canBePreview = [
  ActionType.REMINDER,
  ActionType.ISSUE_STATEMENT_LATE_FEE,
  ActionType.QUOTE_EMAIL,
  ActionType.SEND_STATEMENT,
];

export function ActionPreviewSection({
  action,
}: ActionPreviewSectionProps) {
  const drawerProps = useSelector((s: RootState) => s.actionDrawer);

  const currentOrg = useGetSelectedOrganisation();

  const [regenerateAiEmail, { isLoading: regenerating }] = useRegenerateAiEmailMutation();

  const { data: actionPreviewData, isLoading, isFetching, isUninitialized } = usePreviewActionQuery({
    actionId: drawerProps.actionId,
    organisationId: currentOrg?.id,
  }, {
    // TODO use action config to determine if we should skip
    skip: (
      !drawerProps.open ||
      !drawerProps.actionId ||
      !currentOrg ||
      (action?.status !== ActionStatus.PENDING && action?.status !== ActionStatus.APPROVAL_PENDING) ||
      (!action?.type || !canBePreview.includes(action.type))
      // Todo replace above with below when preview endpoint working
      // (action?.type !== ActionType.REMINDER && action?.type !== ActionType.PAYMENT_PLAN_REMINDER)
    ),
  });

  if (isUninitialized) {
    // Dont display anything if we are uninitialized / not applicable
    return null;
  }

  let content = null;

  if (actionPreviewData?.type === ActionType.REMINDER || actionPreviewData?.type === ActionType.QUOTE_EMAIL) {
    content = (
      <div className={ `min-h-[100px] mt-4` }>
        <div className={ `flex items-center justify-between pb-2` }>
          <Paragraph
            className={ `ml-1` }
          >
            { `Email Preview` }
          </Paragraph>
          { actionPreviewData?.type === ActionType.REMINDER && actionPreviewData.aiGenerated && (
            <Button
              size={ `sm` }
              loading={ regenerating || isFetching }
              disabled={ regenerating || isFetching }
              tooltip={ `Discard this AI email and write a new one. The new email is the one that will be sent.` }
              onClick={ () => regenerateAiEmail({ organisationId: currentOrg.id, actionId: drawerProps.actionId }) }
            >
              { `Regenerate` }
            </Button>
          ) }
        </div>
        <Card className={ `rounded-md shadow-xl border-gray-100 border` }>
          <EmailPreviewDetails
            email={ actionPreviewData?.email }
          />
          <CustomIframe
            srcDoc={ actionPreviewData?.html }
            style={ {
              width: `100%`,
              height: `100%`,
              minHeight: `400px`,
            } }
          />
        </Card>
      </div>
    );
  }

  if (actionPreviewData?.type === ActionType.ISSUE_STATEMENT_LATE_FEE) {
    if (!actionPreviewData?.lineItems || actionPreviewData?.lineItems?.length === 0) {
      content = (
        <div className={ `mt-4` }>
          <Paragraph
            className={ `pb-2 ml-1` }
          >
            { `Charges Preview` }
          </Paragraph>
          <Notification
            type={ `info` }
          >
            { `No charges currently apply.` }
          </Notification>
        </div>
      );
    }

    else {
      content = (
        <div className={ `mt-4` }>
          <Paragraph
            className={ `pb-2 ml-1` }
          >
            { `Charges Preview` }
          </Paragraph>
          <div className={ `p-4 border border-slate-100 rounded-md` }>
            <table className={ `w-full` }>
              <InvoiceLineItems
                lineItems={
                  actionPreviewData?.lineItems || []
                }
                includeAccountCode
              />
            </table>
          </div>
        </div>
      );
    }

  }

  if (actionPreviewData?.type === ActionType.SEND_STATEMENT) {
    content = actionPreviewData.html ?
      (
        <StatementEmailPreview
          html={ actionPreviewData.html }
          emailHtml={ actionPreviewData.emailHtml }
          email={ actionPreviewData.email }
        />
      ) :
      (
        <div className={ `mt-4` }>
          <Paragraph
            className={ `pb-2 ml-1` }
          >
            { `Statement Preview` }
          </Paragraph>
          <Notification
            type={ `info` }
          >
            { actionPreviewData.message || `No statement would currently be sent.` }
          </Notification>
        </div>
      );
  }

  return (
    <LoadingOverlay
      loading={ isLoading }
      className={ isLoading ? `mt-12` : `` }
      message={ `Loading Preview...` }
    >
      { content }
    </LoadingOverlay>
  );
}
